import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ConversionStateService } from './conversion-state.service';
import { LogicielCaisseTicketService } from '../logiciel-caisse/logiciel-caisse-ticket.service';
import { ProductsService } from '../products/products.service';
import { Product } from '../../models/Product';
import { TvaService } from '../../main/parametre/taxe-tva/parametre-tva/tva.service';
import { getEnterpriseStationIdFromUser } from '../enterprise-context';

export interface TicketFactureSnapshot {
  id: number;
  numero: string;
  total: number;
  sousType?: string | null;
  entrepotId?: number | null;
}

/** Acompte / réservation avec paiement : encaissement, pas du CA à facturer. */
function isAcompteSousType(sousType: string | null | undefined): boolean {
  const s = String(sousType || '')
    .trim()
    .toLowerCase();
  return (
    s === 'acompte' ||
    s === 'reservation_avec_paiement' ||
    s === 'reservation_avec_acompte'
  );
}

export interface TicketFactureErpPrepareResult {
  ticket_ids: number[];
  ticket_codes: string[];
  total_ht: number;
  total_ttc: number;
  total_tva: number;
  tva_rate: number;
  client_id: number | null;
  client_type: string;
}

/** Produit financier choisi dans le modal (Recette ou autre type « financier »). */
export interface FinancialProductChoice {
  id: number;
  nom?: string;
  ref_interne?: string;
  tva_id?: number | null;
}

@Injectable({ providedIn: 'root' })
export class TicketFactureErpService {
  constructor(
    private readonly router: Router,
    private readonly conversionState: ConversionStateService,
    private readonly ticketService: LogicielCaisseTicketService,
    private readonly productsService: ProductsService,
    private readonly tvaService: TvaService
  ) {}

  /**
   * Calcule les totaux et ouvre /vente/facture/add-facture avec une ligne « produit financier »
   * (Recette par défaut, ou le produit financier choisi dans le modal).
   */
  openErpFactureFromTickets(
    ticketIds: number[],
    options?: {
      markConsolide?: boolean;
      allowConsolide?: boolean;
      tickets?: TicketFactureSnapshot[];
      financialProduct?: FinancialProductChoice | null;
      etablissementId?: number | null;
      etablissementLibelle?: string | null;
      etablissementCode?: string | null;
      entrepotId?: number | null;
    }
  ): Observable<void> {
    if (!ticketIds.length) {
      return throwError(() => new Error('Aucun ticket sélectionné.'));
    }

    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const erpStationId = getEnterpriseStationIdFromUser(currentUser);
    const erpClientId = Number(currentUser?.id);

    if (!erpStationId || !erpClientId) {
      return throwError(() => new Error('Session ERP invalide (station ou client manquant).'));
    }

    const snapshots$ =
      options?.tickets?.length === ticketIds.length
        ? of(options.tickets)
        : this.loadTicketSnapshots(ticketIds);

    const chosen = options?.financialProduct ?? null;

    return snapshots$.pipe(
      map((snapshots) => this.buildPrepareFromSnapshots(snapshots)),
      switchMap((prepare) =>
        forkJoin({
          prepare: of(prepare),
          tvaList: this.tvaService.getTvaList(erpStationId),
        })
      ),
      switchMap(({ prepare, tvaList }) => {
        const tva19 =
          (tvaList?.tvaListe || []).find(
            (t: { valeur?: number | string }) => Math.abs(Number(t.valeur) - 19) < 0.01
          ) ?? null;
        // Produit financier choisi (déjà existant côté ERP) : utilisé directement.
        // Sinon, on garantit le produit « Recette ».
        const product$ =
          chosen && chosen.id
            ? of({ product: { ...chosen } as unknown as Product, created: false })
            : this.productsService.ensureRecetteProduct(erpStationId, erpClientId, tva19?.id ?? null);
        return product$.pipe(map((recette) => ({ prepare, recette, tva19 })));
      }),
      map(({ prepare, recette, tva19 }) => {
        const ticketLabel = (prepare.ticket_codes || []).join(', ') || prepare.ticket_ids.join(', ');
        const entrepotId =
          options?.entrepotId != null && Number(options.entrepotId) > 0
            ? Number(options.entrepotId)
            : prepare.entrepot_id;
        this.conversionState.set({
          sourceType: 'ticket_caisse_batch',
          mode: 'financial',
          ticketIds: prepare.ticket_ids,
          ticketCodes: prepare.ticket_codes,
          total_ht: prepare.total_ht,
          total_ttc: prepare.total_ttc,
          total_tva: prepare.total_tva,
          tva_rate: prepare.tva_rate ?? 19,
          recetteProduct: recette.product,
          tva19Id: tva19?.id ?? (recette.product as { tva_id?: number | null })?.tva_id ?? null,
          note: `Consolidation tickets caisse : ${ticketLabel}`,
          markConsolide: options?.markConsolide === true,
          etablissementId: options?.etablissementId ?? null,
          etablissementLibelle: options?.etablissementLibelle ?? null,
          etablissementCode: options?.etablissementCode ?? null,
          entrepotId,
        });
        void this.router.navigateByUrl('/vente/facture/add-facture');
      })
    );
  }

  /**
   * Ouvre /vente/facture/add-facture en listant les lignes produits des tickets sélectionnés
   * (au lieu d'une seule ligne financière). Les prix tickets sont TTC → convertis en HT.
   */
  openErpFactureFromTicketsWithProductLines(
    ticketIds: number[],
    options?: {
      markConsolide?: boolean;
      allowConsolide?: boolean;
      etablissementId?: number | null;
      etablissementLibelle?: string | null;
      etablissementCode?: string | null;
      entrepotId?: number | null;
    }
  ): Observable<void> {
    if (!ticketIds.length) {
      return throwError(() => new Error('Aucun ticket sélectionné.'));
    }

    const erpStationId = getEnterpriseStationIdFromUser(
      JSON.parse(localStorage.getItem('currentUser') || '{}')
    );
    if (!erpStationId) {
      return throwError(() => new Error('Session ERP invalide (station manquante).'));
    }

    return forkJoin({
      details: forkJoin(
        ticketIds.map((id) =>
          this.ticketService.getTicketDetail(id).pipe(
            map((data) => ({ id, data }))
          )
        )
      ),
      tvaList: this.tvaService.getTvaList(erpStationId),
    }).pipe(
      map(({ details, tvaList }) => {
        const tva19 =
          (tvaList?.tvaListe || []).find(
            (t: { valeur?: number | string }) => Math.abs(Number(t.valeur) - 19) < 0.01
          ) ?? null;
        const tvaRate = 19;
        const factor = 1 + tvaRate / 100;

        const ticketCodes: string[] = [];
        const lignesProduits: Record<string, unknown>[] = [];
        let totalHt = 0;
        let entrepotId: number | null =
          options?.entrepotId != null && Number(options.entrepotId) > 0
            ? Number(options.entrepotId)
            : null;

        for (const { id, data } of details) {
          const code = String(data['code_document'] ?? `#${id}`);
          if (code) {
            ticketCodes.push(code);
          }
          if (entrepotId == null) {
            const eid = Number(data['entrepot_id']);
            if (Number.isFinite(eid) && eid > 0) {
              entrepotId = eid;
            }
          }
          const sousType = data['sous_type'] != null ? String(data['sous_type']) : null;
          // Acompte / réservation : lié pour consolidation, mais hors lignes CA.
          if (isAcompteSousType(sousType)) {
            continue;
          }
          const lignes = Array.isArray(data['lignes']) ? (data['lignes'] as Record<string, unknown>[]) : [];
          for (const l of lignes) {
            const qnt = Number(l['qnt'] ?? 0);
            // Inclure les qnt négatives (avoirs) ; ignorer uniquement 0 / NaN.
            if (!Number.isFinite(qnt) || qnt === 0) {
              continue;
            }
            const prixTtcRaw = Number(l['prix_unitaire'] ?? l['prix'] ?? 0);
            const prixTtc = Number.isFinite(prixTtcRaw) ? prixTtcRaw : 0;
            const prixHt = Math.round((prixTtc / factor) * 1000) / 1000;
            const totalLigneTtc =
              l['total'] != null && Number.isFinite(Number(l['total']))
                ? Number(l['total'])
                : prixTtc * qnt;
            const totalLigneHt = Math.round((totalLigneTtc / factor) * 1000) / 1000;
            totalHt += totalLigneHt;
            const libelle =
              (l['libelle'] != null && String(l['libelle']).trim() !== ''
                ? String(l['libelle']).trim()
                : String(l['reference'] ?? l['code_barre'] ?? 'Article')) || 'Article';
            lignesProduits.push({
              libelle,
              ref_interne:
                l['code_barre'] != null && String(l['code_barre']).trim() !== ''
                  ? String(l['code_barre']).trim()
                  : '',
              code_barre: l['code_barre'] != null ? String(l['code_barre']).trim() : '',
              couleur: l['couleur'] != null ? String(l['couleur']).trim() : '',
              taille: l['taille'] != null ? String(l['taille']).trim() : '',
              product_id: null,
              qnt,
              prix_ht: prixHt,
              prix_vente: prixHt,
              result_ht: totalLigneHt,
              total_ht: totalLigneHt,
              tva: tvaRate,
              tva_id: tva19?.id ?? null,
              remise: 0,
            });
          }
        }

        const ticketLabel = ticketCodes.join(', ') || ticketIds.join(', ');
        this.conversionState.set({
          sourceType: 'ticket_caisse_batch',
          mode: 'product_lines',
          ticketIds: [...ticketIds],
          ticketCodes,
          total_ht: Math.round(totalHt * 1000) / 1000,
          tva_rate: tvaRate,
          tva19Id: tva19?.id ?? null,
          lignesProduits,
          note: `Lignes produits tickets caisse : ${ticketLabel}`,
          markConsolide: options?.markConsolide === true,
          etablissementId: options?.etablissementId ?? null,
          etablissementLibelle: options?.etablissementLibelle ?? null,
          etablissementCode: options?.etablissementCode ?? null,
          entrepotId,
        });
        void this.router.navigateByUrl('/vente/facture/add-facture');
      })
    );
  }

  private loadTicketSnapshots(ticketIds: number[]): Observable<TicketFactureSnapshot[]> {
    return forkJoin(
      ticketIds.map((id) =>
        this.ticketService.getTicketDetail(id).pipe(
          map((data) => {
            const totalRaw = Number(data['total'] ?? 0);
            const sousRaw = data['sous_type'];
            const eid = Number(data['entrepot_id']);
            return {
              id,
              numero: String(data['code_document'] ?? `#${id}`),
              total: Number.isFinite(totalRaw) ? totalRaw : 0,
              sousType:
                sousRaw != null && String(sousRaw).trim() !== ''
                  ? String(sousRaw).trim()
                  : null,
              entrepotId: Number.isFinite(eid) && eid > 0 ? eid : null,
            };
          })
        )
      )
    );
  }

  private buildPrepareFromSnapshots(
    snapshots: TicketFactureSnapshot[]
  ): TicketFactureErpPrepareResult & { entrepot_id: number | null } {
    // Montant facture = ventes + avoirs ; acomptes exclus (paiement, pas CA).
    const total_ttc = snapshots.reduce((sum, t) => {
      if (isAcompteSousType(t.sousType)) {
        return sum;
      }
      const n = Number(t.total);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const total_ht = +(total_ttc / 1.19).toFixed(3);
    const total_tva = +(total_ttc - total_ht).toFixed(3);
    const entrepot_id =
      snapshots.map((t) => t.entrepotId).find((id) => id != null && Number(id) > 0) ?? null;
    return {
      ticket_ids: snapshots.map((t) => t.id),
      ticket_codes: snapshots.map((t) => t.numero).filter(Boolean),
      total_ht,
      total_ttc,
      total_tva,
      tva_rate: 19,
      client_id: null,
      client_type: 'particulier',
      entrepot_id: entrepot_id != null ? Number(entrepot_id) : null,
    };
  }
}
